import { instagram, OrderingValues } from '@round/api';
import uniq from 'lodash/uniq';
import { isNumber } from 'utility/utility';
import { useSong, useMusicSong } from '@round/creatorbase-helpers/api-hooks';
import { useInstagramAudios, useInstagramPosts } from '@round/platforms/instagram';
import { getMergedAudioIdsFromSongData } from '../../helpers';

type Params = {
    songId?: number | null;
    page: number;
    page_size: number;
    ordering: OrderingValues<instagram.GetInstagramPostsSortableKeys>;
};

export default function useInstagramTopPosts({ songId, page, page_size, ordering }: Params) {
    const { data: song, isLoading: isSongLoading } = useSong(songId);
    const { data: musicSong, isLoading: isMusicSongLoading } = useMusicSong(song?.music_song_id);

    const { instagramAudioIds: audioIds } = getMergedAudioIdsFromSongData(song, musicSong);
    const hasAudioIds = audioIds.length > 0;

    const {
        data: postsData,
        isLoading: isPostsLoading,
        isFetching: isPostsFetching,
        hasError: isError,
    } = useInstagramPosts(
        { page, page_size, ordering, audio_id: audioIds.join(',') },
        {
            enabled: !!song && !isMusicSongLoading && hasAudioIds,
            // preserve count on pagination/ordering changes
            placeholderData: (previousData) => (previousData ? { ...previousData, results: [] } : undefined),
        }
    );

    const postAudioIds = uniq((postsData?.results ?? []).map((post) => post.audio_id).filter(isNumber));

    const { data: audiosResponseData, isLoading: isAudiosLoading } = useInstagramAudios(
        { id: postAudioIds.join(','), page_size: postAudioIds.length },
        { enabled: postAudioIds.length > 0 }
    );

    const audiosData: Record<number, instagram.InstagramAudio | null> = Object.fromEntries(
        (audiosResponseData?.results ?? []).map((audio) => [audio.id, audio])
    );

    const data =
        !hasAudioIds && !!song && !isMusicSongLoading
            ? { count: 0, results: [], next: null, previous: null }
            : postsData ?? null;

    return {
        data,
        audiosData,
        isLoading: isSongLoading || isMusicSongLoading || isPostsLoading || isAudiosLoading,
        isFetching: isPostsFetching,
        isError,
    };
}
